import React, { useState, useEffect } from 'react';
import DemoHeader from './DemoHeader';
import DemoPersonaList from './DemoPersonaList';
import DemoScenarioSelector from './DemoScenarioSelector';
import DemoNarrative from './DemoNarrative';
import DemoMagicBenefits from './DemoMagicBenefits';
import DemoUSPCoverage from './DemoUSPCoverage';
import './DemoPage.css';
import api from '../../services/api';

const DemoPage = () => {
  const [personas, setPersonas] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [usps, setUsps] = useState([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState('harris');
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [personasRes, scenariosRes, uspsRes] = await Promise.all([
          api.get('/demo/personas'),
          api.get('/demo/scenarios'),
          api.get('/demo/usps').catch(() => ({ data: [] })),
        ]);

        setPersonas(personasRes.data);
        setScenarios(scenariosRes.data);
        setUsps(uspsRes.data);

        const harrisScenarios = scenariosRes.data
          .filter(s => s.primaryPersonaId === 'harris')
          .sort((a, b) => a.order - b.order);

        if (harrisScenarios.length > 0) {
          setSelectedScenarioId(harrisScenarios[0].id);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching demo data:', err);
        setError('Failed to load demo data. Please refresh the page.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePersonaSelect = (personaId) => {
    setSelectedPersonaId(personaId);

    const personaScenarios = scenarios
      .filter(s => s.primaryPersonaId === personaId)
      .sort((a, b) => a.order - b.order);

    if (personaScenarios.length > 0) {
      setSelectedScenarioId(personaScenarios[0].id);
    }
  };

  const handleScenarioSelect = (scenarioId) => {
    setSelectedScenarioId(scenarioId);

    const scenario = scenarios.find(s => s.id === scenarioId);
    if (scenario && scenario.primaryPersonaId !== selectedPersonaId) {
      setSelectedPersonaId(scenario.primaryPersonaId);
    }
  };

  const handleRestart = () => {
    setSelectedPersonaId('harris');
    const harrisScenarios = scenarios
      .filter(s => s.primaryPersonaId === 'harris')
      .sort((a, b) => a.order - b.order);

    if (harrisScenarios.length > 0) {
      setSelectedScenarioId(harrisScenarios[0].id);
    }
  };

  const currentScenario = scenarios.find(s => s.id === selectedScenarioId);
  const selectedPersona = personas.find(p => p.id === selectedPersonaId);

  const personaScenarios = scenarios
    .filter(s => s.primaryPersonaId === selectedPersonaId)
    .sort((a, b) => a.order - b.order);

  if (loading) {
    return (
      <div className="demo-page">
        <div className="demo-loading">Loading demo data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="demo-page">
        <div className="demo-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <DemoHeader onRestart={handleRestart} />

      <div className="demo-main-heading">
        Healthcare Platform Demo — SNN Knowledge Graphs, Logic Gates, Cognitive Cortex, EDNL, Explainability & More
      </div>

      <DemoUSPCoverage usps={usps} scenarios={scenarios} />

      <div className="demo-content">
        <div className="demo-column demo-column-left">
          <DemoPersonaList
            personas={personas}
            selectedPersonaId={selectedPersonaId}
            onPersonaSelect={handlePersonaSelect}
          />
        </div>

        <div className="demo-column demo-column-center">
          <DemoScenarioSelector
            scenarios={personaScenarios}
            selectedScenarioId={selectedScenarioId}
            onScenarioSelect={handleScenarioSelect}
          />
          <DemoNarrative
            scenario={currentScenario}
            persona={selectedPersona}
          />
        </div>

        <div className="demo-column demo-column-right">
          <DemoMagicBenefits scenario={currentScenario} />
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
